import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { mcpApi } from "@/api/mcp";
import { queryKeys } from "@/lib/query-keys";
import {
  ArrowLeft,
  Puzzle,
  Wrench,
  Globe,
  Terminal,
  Radio,
} from "lucide-react";

const transportIcons: Record<string, typeof Globe> = {
  http: Globe,
  sse: Radio,
  stdio: Terminal,
};

export function TenantMcpDetail() {
  const { tenantId } = useTenant();
  const { mcpId } = useParams<{ mcpId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: mcpCatalogue = [] } = useQuery({
    queryKey: queryKeys.mcpCatalogue.all,
    queryFn: () => mcpApi.listCatalogue(),
  });
  const { data: tenantMcps = [] } = useQuery({
    queryKey: queryKeys.tenantMcps.byCompany(tenantId),
    queryFn: () => mcpApi.listTenantMcps(tenantId),
  });

  const mcp = tenantMcps.find((m) => m.id === mcpId);
  const cat = mcp?.catalogueMcpId
    ? mcpCatalogue.find((c) => c.id === mcp.catalogueMcpId)
    : null;
  const availableTools = cat?.toolSchemaCache ? Object.keys(cat.toolSchemaCache) : [];
  const toolDescs: Record<string, string> = {};
  if (cat?.toolSchemaCache) {
    for (const [name, schema] of Object.entries(cat.toolSchemaCache)) {
      if (schema && typeof schema === "object" && "description" in schema) {
        toolDescs[name] = String(schema.description);
      }
    }
  }

  const [toolFilter, setToolFilter] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (mcp) {
      setToolFilter(new Set(mcp.allowedTools ?? availableTools));
    }
  }, [mcp?.id, mcp?.allowedTools, availableTools.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateTools = useMutation({
    mutationFn: (allowedTools: string[] | null) =>
      mcpApi.updateTenantMcp(tenantId, mcpId!, { allowedTools }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantMcps.byCompany(tenantId) }),
  });

  if (!mcp) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-sm text-text-muted">MCP not found.</p>
          <Button
            size="sm"
            variant="ghost"
            className="mt-3"
            onClick={() => navigate(`/tenants/${tenantId}/settings/mcp`)}
          >
            <ArrowLeft size={14} className="mr-1.5" />
            Back to MCP list
          </Button>
        </CardContent>
      </Card>
    );
  }

  const Icon = cat ? transportIcons[cat.transportType] || Globe : Puzzle;

  function toggleTool(toolName: string) {
    setToolFilter((prev) => {
      const next = new Set(prev);
      if (next.has(toolName)) next.delete(toolName);
      else next.add(toolName);
      return next;
    });
  }

  function save() {
    if (toolFilter.size === availableTools.length) {
      updateTools.mutate(null); // all enabled → no restriction
    } else {
      updateTools.mutate(Array.from(toolFilter));
    }
  }

  return (
    <div className="space-y-4">
      <Button
        size="sm"
        variant="ghost"
        onClick={() => navigate(`/tenants/${tenantId}/settings/mcp`)}
      >
        <ArrowLeft size={14} className="mr-1.5" />
        Back to MCP list
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Icon size={18} className="text-accent" />
              {mcp.name}
              {cat && (
                <span className="text-xs font-normal text-text-muted">
                  ({cat.transportType.toUpperCase()})
                </span>
              )}
            </span>
            <Badge variant={mcp.enabled ? "success" : "default"}>
              {mcp.enabled ? "enabled" : "disabled"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm font-medium text-text-primary flex items-center gap-2">
              <Wrench size={14} />
              Tool Access Control
            </p>
            <p className="mt-1 text-xs text-text-muted max-w-xl">
              Choose which of this MCP's tools agents in this tenant may call.
              Unchecked tools are hidden from agents even when the MCP is enabled.
            </p>
          </div>

          {availableTools.length === 0 ? (
            <p className="text-xs text-text-muted">
              No tools discovered for this MCP. Test the connection in the MCP Catalogue to discover tools.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs text-text-muted">
                  {toolFilter.size} of {availableTools.length} tools enabled
                </p>
                <div className="flex items-center gap-2">
                  <button
                    className="text-[11px] text-accent hover:underline"
                    onClick={() => setToolFilter(new Set(availableTools))}
                  >
                    Select all
                  </button>
                  <span className="text-text-muted">|</span>
                  <button
                    className="text-[11px] text-accent hover:underline"
                    onClick={() => setToolFilter(new Set())}
                  >
                    Deselect all
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-1">
                {availableTools.map((toolName) => {
                  const isEnabled = toolFilter.has(toolName);
                  return (
                    <label
                      key={toolName}
                      className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-surface-hover"
                    >
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => toggleTool(toolName)}
                        className="h-3.5 w-3.5 rounded border-border accent-accent"
                      />
                      <div className="min-w-0 flex-1">
                        <span className="text-xs font-mono font-medium text-text-primary">
                          {toolName}
                        </span>
                        {toolDescs[toolName] && (
                          <p className="text-[10px] text-text-muted truncate">
                            {toolDescs[toolName]}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Button size="sm" disabled={updateTools.isPending} onClick={save}>
                  Save tool selection
                </Button>
                <span className="text-[11px] text-text-muted">
                  {toolFilter.size === availableTools.length
                    ? "All tools enabled (no restriction)"
                    : `${toolFilter.size} specific tool(s) allowed`}
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
